import { z } from 'zod';

/* eslint-disable no-console */

const LegacyStatusSchema = z.enum(['processing', 'completed', 'failed']);
const CeleryStatusSchema = z.enum([
  'PENDING',
  'STARTED',
  'RETRY',
  'SUCCESS',
  'FAILURE',
  'CANCELED',
]);

const TaskStatusValueSchema = z.union([LegacyStatusSchema, CeleryStatusSchema]);

const KNOWN_TASK_PREFIXES = new Set(['ocr', 'email', 'celery']);

export interface ParsedTaskType {
  taskType: string;
  taskSubtype: string;
}

export type NormalizedTaskStatus =
  | 'queued'
  | 'processing'
  | 'retrying'
  | 'success'
  | 'failure'
  | 'canceled';

const STATUS_NORMALIZATION_MAP: Record<string, NormalizedTaskStatus> = {
  PENDING: 'queued',
  STARTED: 'processing',
  RETRY: 'retrying',
  SUCCESS: 'success',
  FAILURE: 'failure',
  CANCELED: 'canceled',
  processing: 'processing',
  completed: 'success',
  failed: 'failure',
};

export function normalizeTaskStatus(status: string): NormalizedTaskStatus {
  const normalized = STATUS_NORMALIZATION_MAP[status];
  if (!normalized) {
    console.warn('[TaskStatus] Unknown status received from server', status);
    return 'processing';
  }
  return normalized;
}

export function parseTaskType(type: string): ParsedTaskType {
  if (!type || typeof type !== 'string') {
    console.warn('[TaskStatus] Missing type, defaulting to unknown');
    return { taskType: 'unknown', taskSubtype: 'unknown' };
  }

  const [category, ...rest] = type.split('.');
  if (!category) {
    console.warn('[TaskStatus] Unable to parse task type from', type);
    return { taskType: 'unknown', taskSubtype: 'unknown' };
  }

  const normalizedCategory = KNOWN_TASK_PREFIXES.has(category)
    ? category
    : 'unknown';
  if (normalizedCategory === 'unknown') {
    console.warn(
      '[TaskStatus] Unknown task category',
      category,
      'for type',
      type,
    );
  }

  const subtype = rest.length > 0 ? rest.join('.') : 'unknown';

  return {
    taskType: normalizedCategory,
    taskSubtype: subtype,
  };
}

export type TaskType = ParsedTaskType['taskType'];
export type TaskSubtype = ParsedTaskType['taskSubtype'];

export const TaskStatusResponseSchema = z
  .object({
    taskId: z
      .string()
      .min(1, { message: 'taskId must be provided by the server response' }),
    type: z
      .string()
      .min(1, { message: 'type must contain a dot-delimited task descriptor' }),
    status: TaskStatusValueSchema,
    resultId: z.number().int().nullable().optional(),
    error: z.string().nullable().optional(),
    // The backend always serializes this key and sends `null` when a task has
    // no extra context. `.optional()` alone rejects `null`, which made every
    // OCR status poll throw and silently stalled task tracking (e.g. the
    // post-scan auto-navigation never fired). Accept null as well as undefined.
    details: z.record(z.unknown()).nullish(),
    queuedAt: z.string().datetime().optional(),
    startedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().optional(),
  })
  .strict();

export const TaskStatusListResponseSchema = z
  .array(TaskStatusResponseSchema)
  .readonly();

export type TaskStatusPayload = z.infer<typeof TaskStatusResponseSchema>;

export interface ParsedTaskStatusResponse
  extends TaskStatusPayload,
    ParsedTaskType {
  normalizedStatus: NormalizedTaskStatus;
}

export type ParsedTaskStatusListResponse = readonly ParsedTaskStatusResponse[];

export function parseTaskStatusResponse(
  data: unknown,
): ParsedTaskStatusResponse {
  const payload = TaskStatusResponseSchema.parse(data);
  const derived = parseTaskType(payload.type);
  return {
    ...payload,
    ...derived,
    normalizedStatus: normalizeTaskStatus(payload.status),
  };
}

export function parseTaskStatusListResponse(
  data: unknown,
): ParsedTaskStatusListResponse {
  const payload = TaskStatusListResponseSchema.parse(data);
  return payload.map(parseTaskStatusResponse);
}
